<template>
  <div class="pr-4 pb-16 max-w-2xl">
    <h1 class="text-2xl uppercase font-semibold text-white">
      {{ $t("account.settings") }}
    </h1>
    <AccountTabs />

    <!-- Shared save status for the whole page: every control here saves
         immediately, so one status line beats three. -->
    <p
      v-if="status"
      role="status"
      class="mt-6 text-sm"
      :class="statusError ? 'text-red-400' : 'text-highlight'"
    >
      {{ status }}
    </p>

    <!-- Language -->
    <section class="mt-10">
      <h2 class="text-lg font-semibold text-white">
        {{ $t("account.language") }}
      </h2>
      <p class="text-subtle text-sm mt-1">{{ $t("account.languageHint") }}</p>

      <div
        class="mt-4 flex flex-wrap gap-2"
        role="group"
        :aria-label="$t('account.language')"
      >
        <button
          v-for="option in languages"
          :key="option.value"
          type="button"
          class="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border transition"
          :class="
            locale === option.value
              ? 'bg-highlight border-highlight text-primary-700 font-semibold'
              : 'bg-primary-500 border-primary-400 text-muted hover:text-highlight'
          "
          :aria-pressed="locale === option.value ? 'true' : 'false'"
          @click="selectLocale(option.value)"
        >
          <img :src="option.flag" alt="" class="h-4 w-6 object-cover rounded" />
          {{ option.label }}
        </button>
      </div>
    </section>

    <!-- Notifications -->
    <section class="mt-14 border-t border-primary-400 pt-10">
      <h2 class="text-lg font-semibold text-white">
        {{ $t("account.notifications") }}
      </h2>
      <p class="text-subtle text-sm mt-1">
        {{ $t("account.notificationsHint") }}
      </p>

      <ul class="mt-6 flex flex-col divide-y divide-primary-400">
        <li
          v-for="channel in channels"
          :key="channel.key"
          class="flex items-center gap-4 py-4"
        >
          <Font-awesome-icon
            :icon="['fas', channel.icon]"
            class="w-5 flex-none"
            :class="channel.available ? 'text-highlight' : 'text-subtle'"
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1">
            <p
              class="text-sm"
              :class="channel.available ? 'text-white' : 'text-muted'"
            >
              {{ $t(`account.channel.${channel.key}`) }}
              <span
                v-if="!channel.available"
                class="ml-2 inline-block rounded-full bg-primary-500 px-2 py-0.5 text-[11px] uppercase tracking-wide text-subtle align-middle"
              >
                {{ $t("account.comingFeature") }}
              </span>
            </p>
            <p class="text-subtle text-xs mt-1">
              {{ $t(`account.channelHint.${channel.key}`) }}
            </p>
          </div>
          <Toggle
            :value="notifications[channel.key]"
            :disabled="!channel.available || saving"
            :label="$t(`account.channel.${channel.key}`)"
            @change="setChannel(channel.key, $event)"
          />
        </li>
      </ul>
    </section>

    <!-- Subscription -->
    <section class="mt-14 border-t border-primary-400 pt-10">
      <h2 class="text-lg font-semibold text-white">
        {{ $t("account.subscription") }}
      </h2>
      <p class="text-subtle text-sm mt-1">
        {{ $t("account.subscriptionHint") }}
      </p>

      <div class="mt-6 grid gap-4 sm:grid-cols-2">
        <article
          v-for="plan in plans"
          :key="plan.value"
          class="rounded-xl border p-5 flex flex-col"
          :class="
            subscription === plan.value
              ? 'border-highlight bg-primary-500'
              : 'border-primary-400 bg-primary-500/40'
          "
        >
          <div class="flex items-center justify-between">
            <h3 class="text-white font-semibold uppercase tracking-widest">
              {{ $t(plan.label) }}
            </h3>
            <span
              v-if="subscription === plan.value"
              class="rounded-full bg-highlight px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-700"
            >
              {{ $t("account.currentPlan") }}
            </span>
          </div>

          <ul class="mt-4 flex-1 space-y-2 text-sm text-muted">
            <li
              v-for="feature in plan.features"
              :key="feature"
              class="flex items-start gap-2"
            >
              <Font-awesome-icon
                :icon="['fas', 'circle-check']"
                class="mt-0.5 flex-none text-highlight"
                aria-hidden="true"
              />
              <span>{{ $t(feature) }}</span>
            </li>
          </ul>

          <Button
            v-if="subscription !== plan.value"
            class="mt-6 uppercase tracking-widest"
            :loading="saving && pendingPlan === plan.value"
            @click.prevent="selectPlan(plan.value)"
          >
            {{ $t(plan.action) }}
          </Button>
          <p v-else class="mt-6 text-subtle text-sm">
            {{ $t("account.planActive") }}
          </p>
        </article>
      </div>

      <!-- Downgrading below the free limit needs an explanation, not a
           silent failure the next time they try to track something. -->
      <p v-if="willExceedFreeLimit" class="mt-4 text-subtle text-sm">
        <Font-awesome-icon
          :icon="['fas', 'circle-exclamation']"
          class="text-highlight mr-1"
          aria-hidden="true"
        />
        {{ $t("account.downgradeWarning", { count: trackedCount }) }}
      </p>
    </section>
  </div>
</template>

<script>
import AccountTabs from "@/components/account/AccountTabs.vue";
import Button from "@/components/common/Button.vue";
import Toggle from "@/components/common/Toggle.vue";
import { Locale } from "@/i18n";
import {
  Subscription,
  NOTIFICATION_CHANNELS,
  FREE_SERIES_LIMIT,
} from "@/utils/user";
// The flags live in src/assets, so they must go through the bundler.
import flagUk from "@/assets/flag_uk.png";
import flagSwe from "@/assets/flag_swe.png";

const CHANNEL_ICONS = { email: "envelope", app: "bell", sms: "comment-sms" };

export default {
  name: "Settings",
  components: { AccountTabs, Button, Toggle },

  data() {
    return {
      saving: false,
      pendingPlan: null,
      status: "",
      statusError: false,
      languages: [
        { value: Locale.ENGLISH, label: "English", flag: flagUk },
        { value: Locale.SWEDISH, label: "Svenska", flag: flagSwe },
      ],
      plans: [
        {
          value: Subscription.FREE,
          label: "account.planFree",
          action: "account.switchToFree",
          features: ["account.featureFreeLimit", "account.featureFreeBasics"],
        },
        {
          value: Subscription.PRO,
          label: "account.planPro",
          action: "account.upgradeToPro",
          features: ["account.featureProUnlimited", "account.featureProSoon"],
        },
      ],
    };
  },

  computed: {
    locale() {
      return this.$store.getters.userLocale;
    },
    notifications() {
      return this.$store.getters.notificationSettings;
    },
    subscription() {
      return this.$store.getters.subscription;
    },
    channels() {
      return NOTIFICATION_CHANNELS.map((channel) => ({
        ...channel,
        icon: CHANNEL_ICONS[channel.key],
      }));
    },
    trackedCount() {
      return Object.keys(this.$store.getters.seriesList).length;
    },
    willExceedFreeLimit() {
      return (
        this.subscription === Subscription.PRO &&
        this.trackedCount > FREE_SERIES_LIMIT
      );
    },
  },

  methods: {
    selectLocale(value) {
      if (value === this.locale) return;
      // Apply straight away — waiting for a round trip to change the UI
      // language would feel broken.
      this.$root.$i18n.locale = value;
      document.documentElement.setAttribute("lang", value);
      this.persist({ locale: value });
    },

    setChannel(key, enabled) {
      this.persist({
        notifications: { ...this.notifications, [key]: enabled },
      });
    },

    selectPlan(value) {
      if (value === this.subscription) return;
      this.pendingPlan = value;
      this.persist({ subscription: value });
    },

    persist(settings) {
      this.saving = true;
      this.status = "";
      this.statusError = false;

      this.$store
        .dispatch("updateSettings", settings)
        .then(() => {
          this.status = this.$t("account.saved").toString();
          this.statusError = false;
        })
        .catch(() => {
          // The change is already applied locally; say so honestly rather
          // than pretending it saved or silently reverting it.
          this.status = this.$t("account.saveFailed").toString();
          this.statusError = true;
        })
        .finally(() => {
          this.saving = false;
          this.pendingPlan = null;
        });
    },
  },
};
</script>
